import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Btn, Modal, Input, Select, Textarea, FormGrid, SectionHeader, Badge, Tabs, RNLLoader, Spinner, HeaderBack } from '../components/UI';
import { statusColor, exportCSV, hasRole, can, adminAccess, ADMIN_CARDS, errorText, userAdminAccess, canManageUser } from '../utils';
import * as api from '../api';
import Avatar from '../components/Avatar';
import AdminProjectsTab from './AdminProjectsTab';
import AdminWorkflowsTab from './AdminWorkflowsTab';
import ComingSoonPage from './ComingSoonPage';
import RequestManagementTab from './RequestManagementTab';
import UserAccessControl from './UserAccessControl';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Pagination from '../components/requisition/Pagination';
import MaterialImportModal, { downloadTemplate } from '../components/admin/MaterialImportModal';
import * as XLSX from 'xlsx';

// A crossed-out bin, so a permanent delete cannot be mistaken at a glance for
// the reversible deactivate beside it.
const PurgeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    <line x1="10" y1="11" x2="14" y2="15" /><line x1="14" y1="11" x2="10" y2="15" />
  </svg>
);

const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);


/**
 * Toolbar for a list tab: a search box on the left, the tab's own action on
 * the right, and a live "N of M" while a search is active.
 *
 * One component rather than a copy per tab, so the three admin lists cannot
 * drift into looking or behaving differently from each other.
 */
const ListToolbar = ({ value, onChange, onKeyDown, onSearch, placeholder, shown, total, filterActive, children }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: 12, marginBottom: 14, flexWrap: 'wrap',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 420px', maxWidth: 700 }}>
    <div style={{ position: 'relative', flex: 1 }}>
      <span style={{
        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
        display: 'flex', alignItems: 'center', pointerEvents: 'none', color: 'var(--text-muted)',
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" /><line x1="20" y1="20" x2="16.65" y2="16.65" />
        </svg>
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 34px 9px 34px',
          border: '1.5px solid var(--border-strong, #94a3b8)', borderRadius: 10,
          fontSize: 13, fontFamily: 'var(--font)',
          background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = 'var(--accent)';
          e.target.style.boxShadow = 'var(--ring)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = 'var(--border-strong, #94a3b8)';
          e.target.style.boxShadow = 'none';
        }}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          title="Clear search"
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
            color: 'var(--text-muted)', lineHeight: 1, padding: 4,
          }}
        >&times;</button>
      )}
    </div>
    {onSearch && (
      <button
        onClick={onSearch}
        style={{ height: 38, padding: '0 18px', background: '#0b318b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Search
      </button>
    )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {filterActive && (
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {shown} of {total}
        </span>
      )}
      {children}
    </div>
  </div>
);

/** Case-insensitive match of a query against any of the given values. */
const matchesQuery = (q, values) =>
  !q || values.some((v) => (v ?? '').toString().toLowerCase().includes(q));

// ─── USERS TAB ───────────────────────────────
function UsersTab({ toast, me }) {
  const ua = userAdminAccess(me);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  // Permanent delete: { user, impact } once the preview has loaded.
  const [purge, setPurge] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState(null);
  // The user_id whose impact is being counted, so only the button that was
  // clicked spins — not every delete button in the table.
  const [purgePreparing, setPurgePreparing] = useState(null);
  const [search, setSearch] = useState('');
  const [committed, setCommitted] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const u = await api.listUsers(page.skip, page.limit, committed);
      setUsers(Array.isArray(u) ? u : []);
      setTotalCount(u?.totalCount ?? (Array.isArray(u) ? u.length : 0));
    } catch { }
    setLoading(false);
  }, [page, committed]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => {
    setCommitted(search.trim());
    setPage(p => ({ ...p, skip: 0 }));
  };

  // The impact is fetched BEFORE the dialog opens, so the admin never sees
  // an empty confirmation that fills in afterwards — this is the one dialog
  // where reading the numbers before deciding actually matters.
  const openHardDelete = async (row) => {
    if (purgePreparing) return;
    setPurgeError(null); setPurgeConfirm('');
    // Counting the impact walks a dozen tables, so the wait is visible. It
    // shows on the button that was pressed rather than letting the dialog
    // appear empty and fill in — the counts are the whole point of that
    // dialog, so it must not open before they exist.
    setPurgePreparing(row.user_id);
    try {
      const impact = await api.previewUserHardDelete(row.user_id);
      setPurge({ user: row, impact });
    } catch (e) {
      toast.show(e.message || 'Could not check what this would delete.', 'error');
    }
    setPurgePreparing(null);
  };

  const runHardDelete = async () => {
    if (!purge || purging) return;
    setPurging(true); setPurgeError(null);
    try {
      const res = await api.hardDeleteUser(purge.user.user_id, purgeConfirm.trim());
      setPurge(null);
      toast.show(res?.message || 'User permanently deleted');
      load();
    } catch (e) {
      setPurgeError(e.message || 'Could not delete this user.');
    }
    setPurging(false);
  };

  const del = async (id) => {
    try { await api.deleteUser(id); toast.show('User deactivated'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
    setDeleteModal(null);
  };

  const visibleUsers = users;

  return (
    <>
      <ListToolbar
        value={search}
        onChange={v => { setSearch(v); if (!v) { setCommitted(''); setPage(p => ({ ...p, skip: 0 })); } }}
        onKeyDown={e => e.key === 'Enter' && handleSearch()}
        onSearch={handleSearch}
        placeholder="Search by name, email, phone, employee ID, job title or role…"
        shown={totalCount} total={totalCount}
        filterActive={!!committed}
      >
        {ua.create && <Btn onClick={() => navigate('/admin/users/new')}>New User</Btn>}
      </ListToolbar>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          {
            key: 'first_name', label: 'Name', render: (v, row) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                <Avatar first={row.first_name} last={row.last_name} src={row.profile_pic} size={30} />
                <strong style={{ whiteSpace: 'nowrap' }}>{row.first_name} {row.last_name}</strong>
              </div>
            )
          },
          { key: 'email', label: 'Email', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{v}</span> },
          { key: 'phone', label: 'Phone', render: v => v || '—' },
          { key: 'employee_id', label: 'Emp ID', render: v => v || '—' },
          { key: 'job_title', label: 'Job Title', render: v => v || '—' },
          {
            key: 'is_active', label: 'Status', render: (v, row) => (
              // Locked wins the single badge slot: it is the state an admin
              // has to act on, and an account can be locked while still active.
              row.is_locked
                ? <Badge color="red" pulse>Locked</Badge>
                : <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge>
            )
          },
          { key: 'is_verified', label: 'Verified', render: v => <Badge color={v ? 'green' : 'amber'}>{v ? 'Yes' : 'Pending'}</Badge> },
          {
            key: 'user_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="ghost" title="Open full profile" disabled={!!purgePreparing}
                  style={{ padding: '6px', opacity: purgePreparing ? 0.45 : 1 }}
                  onClick={() => navigate(`/admin/users/${id}`)}><EyeIcon /></Btn>
                {canManageUser(me, row) && (ua.edit || ua.assign_roles || ua.deactivate || ua.mfa) && (
                <Btn variant="ghost" title="Edit User" disabled={!!purgePreparing}
                  style={{ padding: '6px', opacity: purgePreparing ? 0.45 : 1 }}
                  onClick={() => navigate(`/admin/users/${id}/edit`)}><EditIcon /></Btn>
                )}
                {/* Users are deactivated, never deleted — they're referenced by the
                    approval audit trail and financial records. */}
                {!(ua.deactivate && canManageUser(me, row)) ? null : row.is_active === false ? (
                  <Btn variant="primary" title="Reactivate User" disabled={!!purgePreparing}
                    style={{ padding: '6px 10px', fontSize: 12, opacity: purgePreparing ? 0.45 : 1 }}
                    onClick={async () => { await api.updateUser(id, { is_active: true }); toast.show('User reactivated'); load(); }}>
                    Reactivate
                  </Btn>
                ) : (
                  <Btn variant="danger" title="Deactivate User" disabled={!!purgePreparing}
                    style={{ padding: '6px', opacity: purgePreparing ? 0.45 : 1 }}
                    onClick={() => setDeleteModal(id)}><TrashIcon /></Btn>
                )}
                {/* Permanent delete, beside deactivate. Deactivate is
                    reversible; this is not, so it gets a darker treatment and
                    a typed confirmation. */}
                {ua.isSuper && (
                <Btn
                  variant="danger"
                  disabled={!!purgePreparing}
                  title={purgePreparing === id
                    ? 'Checking what would be deleted…'
                    : 'Permanently delete this user and their records'}
                  style={{
                    padding: '6px', background: '#7f1d1d', color: '#fff',
                    border: '1px solid #7f1d1d',
                    cursor: purgePreparing === id ? 'wait' : (purgePreparing ? 'not-allowed' : 'pointer'),
                    opacity: purgePreparing && purgePreparing !== id ? 0.45 : 1,
                  }}
                  onClick={() => openHardDelete(row)}
                >
                  {purgePreparing === id ? <Spinner size={15} /> : <PurgeIcon />}
                </Btn>
                )}
              </div>
            )
          },
        ]} rows={visibleUsers}
          emptyMsg={committed ? `No users match "${committed}".` : 'No users found.'} />
        <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
      </Card>

      {purge && (() => {
        const u = purge.user;
        const email = purge.impact?.user?.email || u.email || '';
        const matches = purgeConfirm.trim().toLowerCase() === email.toLowerCase();
        const { deleted = [], detached = [], total_deleted = 0, total_detached = 0 } = purge.impact || {};
        const rowLine = (d, i, colour) => (
          <div key={d.table + d.label} style={{
            display: 'flex', justifyContent: 'space-between', gap: 12,
            padding: '8px 12px', fontSize: 12.5,
            borderTop: i ? '1px solid var(--border)' : 'none',
          }}>
            <span style={{ color: 'var(--text-primary)' }}>{d.label}</span>
            <strong style={{ color: colour }}>{d.count}</strong>
          </div>
        );
        return (
          <Modal title="Permanently Delete User" width={620}
            headerBg="#7f1d1d" headerColor="#ffffff"
            onClose={purging ? () => { } : () => setPurge(null)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>
              <div style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.35)',
                borderRadius: 8, padding: '12px 14px',
              }}>
                <span style={{ fontSize: 18, lineHeight: 1.1 }}>&#9888;</span>
                <div style={{ fontSize: 13, color: 'var(--red)', lineHeight: 1.6 }}>
                  <strong>This cannot be undone.</strong> {u.first_name} {u.last_name} and the records
                  below will be erased from the database. There is no recovery short of a backup restore.
                </div>
              </div>

              {total_deleted > 0 ? (
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                    Will be deleted ({total_deleted})
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    {deleted.map((d, i) => rowLine(d, i, 'var(--red)'))}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  This user has no records of their own — only the account itself will be removed.
                </div>
              )}

              {total_detached > 0 && (
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                    Kept, but will no longer name them ({total_detached})
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', maxHeight: 150, overflowY: 'auto' }}>
                    {detached.map((d, i) => rowLine(d, i, 'var(--text-muted)'))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                    These belong to other people — approval steps on their requisitions, projects,
                    vouchers. They survive with this person&apos;s name cleared.
                  </div>
                </div>
              )}

              <div>
                <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
                  Type <span style={{ fontFamily: 'var(--mono)', color: 'var(--red)' }}>{email}</span> to confirm
                </label>
                <Input value={purgeConfirm} onChange={setPurgeConfirm} placeholder={email} />
              </div>

              {purgeError && (
                <div style={{ fontSize: 12.5, color: 'var(--red)', background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', lineHeight: 1.55 }}>
                  {purgeError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn variant="ghost" disabled={purging} onClick={() => setPurge(null)}>Cancel</Btn>
                <Btn variant="danger" loading={purging} disabled={!matches}
                  style={{ background: '#7f1d1d', color: '#fff', border: '1px solid #7f1d1d' }}
                  onClick={runHardDelete}>
                  {/* Btn renders the spinner itself when `loading`; the label
                      changes alongside it so the wait is named, not just
                      animated. */}
                  {purging ? 'Deleting…' : 'Delete Permanently'}
                </Btn>
              </div>
            </div>
          </Modal>
        );
      })()}

      {deleteModal && (
        <Modal title="Deactivate User" onClose={() => setDeleteModal(null)} width={440} headerBg="#0b318bff" headerColor="#ffffff">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '10px 0' }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
              Are you sure you want to deactivate this user? You can reactivate them at any time.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <Btn variant="ghost" onClick={() => setDeleteModal(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={() => del(deleteModal)}>Deactivate</Btn>
            </div>
          </div>
        </Modal>
      )}

    </>
  );
}

// ─── ROLES TAB ───────────────────────────────
function RolesTab({ toast }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await api.listRoles(); setRoles(Array.isArray(r) ? r : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  const visibleRoles = roles.filter((r) => matchesQuery(q, [
    r.display_name, r.role_name, r.description,
  ]));

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, name: form.role_name };
      if (modal === 'create') await api.createRole(payload);
      else await api.updateRole(form.role_id, payload);
      toast.show('Role saved'); setModal(null); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  return (
    <>
      <ListToolbar
        value={search} onChange={setSearch}
        placeholder="Search by role name or description…"
        shown={visibleRoles.length} total={roles.length}
      >
        <Btn onClick={() => { setForm({}); setModal('create'); }}>New Role</Btn>
      </ListToolbar>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'role_name', label: 'Role Name', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'display_name', label: 'Display Name', render: v => <strong>{v}</strong> },
          { key: 'description', label: 'Description', render: v => <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'permissions', label: 'Permissions', render: v => `${v?.length || 0} assigned` },
          {
            key: 'role_id', label: '', render: (id, row) => (
              <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal('edit'); }}>Edit</Btn>
            )
          },
        ]} rows={visibleRoles}
          emptyMsg={q ? `No roles match "${search.trim()}".` : 'No roles found.'} />
      </Card>

      {modal && (
        <Modal title={modal === 'create' ? 'New Role' : 'Edit Role'} onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {modal === 'create' && <Input label="Role Name (no spaces)" required value={form.role_name || ''} onChange={set('role_name')} placeholder="e.g. quantity_surveyor" />}
            <Input label="Display Name" required value={form.display_name || ''} onChange={set('display_name')} placeholder="e.g. Quantity Surveyor" />
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── DEPARTMENTS TAB ─────────────────────────
function DepartmentsTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [res, u] = await Promise.all([api.listDepartments(), api.listUsers()]);
      let data = Array.isArray(res) ? res : (res.departments || res.data || []);
      // Sort active first, inactive last
      data.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(data);
      setUsers(Array.isArray(u) ? u : []);
    }
    catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  const visibleDepartments = rows.filter((d) => matchesQuery(q, [
    d.name, d.description, d.department_head_name,
  ]));

  const userOptions = [
    { value: '', label: '— None —' },
    ...users.map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name} (${u.email})` }))
  ];

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, department_head_id: form.department_head_id || null };
      if (form.id) await api.updateDepartment(form.id, payload);
      else await api.createDepartment(payload);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteDepartment(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateDepartment(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <ListToolbar
        value={search} onChange={setSearch}
        placeholder="Search by department name, description or head…"
        shown={visibleDepartments.length} total={rows.length}
      >
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Department</Btn>}
      </ListToolbar>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'department_head_name', label: 'Department Head', render: v => v || <span style={{ color: '#94a3b8' }}>— Not set —</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}>Deactivate</Btn>)
                ) : (
                  (access.edit && <Btn variant="success" size="sm" style={{ padding: '3px 8px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          },
        ]} rows={visibleDepartments}
          emptyMsg={q ? `No departments match "${search.trim()}".` : 'No departments found.'} />
      </Card>

      {modal && (
        <Modal title={form.id ? "Edit Department" : "New Department"} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Name" required value={form.name || ''} onChange={set('name')} placeholder="e.g. Engineering" />
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <Select
              label="Department Head"
              value={form.department_head_id || ''}
              onChange={set('department_head_id')}
              options={userOptions}
            />
            {form.id && (
              <Select label="Active" value={form.is_active ? 'yes' : 'no'} onChange={v => setForm(f => ({ ...f, is_active: v === 'yes' }))}
                options={[{ value: 'yes', label: 'Active' }, { value: 'no', label: 'Inactive' }]} />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              Are you sure you want to {confirmModal.action} this department?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)} disabled={saving}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} loading={saving}>
                Yes, {confirmModal.action}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── PURCHASE REQUEST TYPES TAB ─────────────────────────
// ─── CONSIDERATION STATUSES TAB ───────────────
// The "why is this parked" reasons the Cost Control queue groups by. Separate
// from a request's workflow status on purpose — see the ConsiderationStatus
// model. Configurable so the client can add reasons without a code change.
function ConsiderationStatusesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listConsiderationStatuses();
      const data = Array.isArray(res) ? res : (res.consideration_statuses || []);
      setRows(data);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        color: form.color || null,
        icon: form.icon || null,
        sort_order: Number(form.sort_order) || 0,
        is_return_reason: !!form.is_return_reason,
        is_active: form.is_active !== false,
      };
      if (form.id) await api.updateConsiderationStatus(form.id, payload);
      else await api.createConsiderationStatus(payload);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteConsiderationStatus(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateConsiderationStatus(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
    setConfirmModal(null);
  };

  const COLORS = ['blue', 'green', 'amber', 'red', 'purple', 'cyan', 'indigo', 'gray'];
  const dim = (row) => !row.is_active;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 680 }}>
          Reasons a requisition is parked, used to group the Cost Control queue. Independent of
          the workflow status — a request can be <strong>Returned</strong> and awaiting a BOQ
          clarification at the same time. Only entries marked <strong>Return reason</strong> are
          offered when a reviewer returns a request.
        </div>
        {access.add && <Btn onClick={() => { setForm({ is_active: true, is_return_reason: true, sort_order: (rows.length + 1) * 10 }); setModal(true); }}>
          Add Consideration Status
        </Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          {
            key: 'name', label: 'Name', render: (v, row) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15 }}>{row.icon || '•'}</span>
                <strong style={{ textDecoration: dim(row) ? 'line-through' : 'none', color: dim(row) ? '#94a3b8' : 'inherit' }}>{v}</strong>
              </span>
            )
          },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: dim(row) ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'color', label: 'Colour', render: v => v ? <Badge color={v}>{v}</Badge> : <span style={{ color: 'var(--text-muted)' }}>—</span> },
          { key: 'sort_order', label: 'Order', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{v}</span> },
          {
            key: 'is_return_reason', label: 'Return reason', render: v => (
              <Badge color={v ? 'purple' : 'gray'}>{v ? 'Selectable' : 'System only'}</Badge>
            )
          },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px 10px', fontSize: 12 }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? 'Edit Consideration Status' : 'New Consideration Status'} onClose={() => setModal(false)} width={620}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid cols={2}>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} placeholder="e.g. Budget Not Available" />
              <Input label="Icon (emoji)" value={form.icon || ''} onChange={set('icon')} placeholder="⛔" />
              <Select label="Colour" value={form.color || ''} onChange={set('color')}
                options={[{ value: '', label: '— None —' }, ...COLORS.map(c => ({ value: c, label: c }))]} />
              <Input label="Sort Order" value={form.sort_order ?? ''} onChange={set('sort_order')} placeholder="10" />
            </FormGrid>
            <Textarea label="Description" rows={2} value={form.description || ''} onChange={set('description')}
              placeholder="Shown to reviewers when they pick this reason." />

            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              padding: '12px 14px', borderRadius: 8,
            }}>
              <input type="checkbox" style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                checked={!!form.is_return_reason}
                onChange={e => setForm(f => ({ ...f, is_return_reason: e.target.checked }))} />
              <span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Offer this as a return reason
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5 }}>
                  {form.is_return_reason
                    ? 'Reviewers can pick this when returning a request.'
                    : 'Set by the system as a request moves — not offered in the return dialog.'}
                </span>
              </span>
            </label>

            <Select label="Active" value={form.is_active === false ? 'no' : 'yes'}
              onChange={v => setForm(f => ({ ...f, is_active: v === 'yes' }))}
              options={[{ value: 'yes', label: 'Active' }, { value: 'no', label: 'Inactive' }]} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving} disabled={!form.name}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title={confirmModal.action === 'deactivate' ? 'Deactivate Status' : 'Activate Status'} onClose={() => setConfirmModal(null)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '10px 0' }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {confirmModal.action === 'deactivate'
                ? 'This stops the status being offered on new returns. Requests already carrying it keep it, so nothing is lost from the record.'
                : 'This status will be offered again.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} loading={saving} onClick={confirmAction}>
                {confirmModal.action === 'deactivate' ? 'Deactivate' : 'Activate'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function PRTypesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listPurchaseRequestTypes();
      let data = Array.isArray(res) ? res : (res.types || res.data || []);
      // Sort active first, inactive last
      data.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(data);
    }
    catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updatePurchaseRequestType(form.id, form);
      else await api.createPurchaseRequestType(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deletePurchaseRequestType(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updatePurchaseRequestType(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Request Type</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? "Edit Purchase Request Type" : "New Request Type"} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Name</label>
              <Input autoFocus value={form.name || ''} onChange={set('name')} />
            </div>
            <div>
              <label>Description</label>
              <Textarea rows={3} value={form.description || ''} onChange={set('description')} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              Are you sure you want to {confirmModal.action} this purchase request type?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)} disabled={saving}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} loading={saving}>
                Yes, {confirmModal.action}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── VOUCHER CREATION METHODS TAB ────────────────────────
function PaymentMethodsTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listPaymentVoucherMethods();
      let data = Array.isArray(res) ? res : (res.methods || res.data || []);
      // Sort active first, inactive last
      data.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(data);
    }
    catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updatePaymentVoucherMethod(form.id, form);
      else await api.createPaymentVoucherMethod(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deletePaymentVoucherMethod(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updatePaymentVoucherMethod(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Voucher Method</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? "Edit Voucher Creation Method" : "New Voucher Creation Method"} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Name</label>
              <Input autoFocus value={form.name || ''} onChange={set('name')} placeholder="e.g. Reimbursement Voucher, Petty Cash / Imprest Voucher" />
            </div>
            <div>
              <label>Description</label>
              <Textarea rows={3} value={form.description || ''} onChange={set('description')} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              Are you sure you want to {confirmModal.action} this voucher creation method?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)} disabled={saving}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} loading={saving}>
                Yes, {confirmModal.action}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── PURCHASE REQUEST LEDGER TAB ─────────────────────────
function LedgersTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listLedgers();
      let data = Array.isArray(res) ? res : (res.ledgers || res.data || []);
      // Sort active first, inactive last
      data.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(data);
    }
    catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updateLedger(form.id, form);
      else await api.createLedger(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteLedger(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateLedger(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Ledger</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? "Edit Ledger" : "New Ledger"} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Name</label>
              <Input autoFocus value={form.name || ''} onChange={set('name')} placeholder="e.g. Basic Salary" />
            </div>
            <div>
              <label>Description</label>
              <Textarea rows={3} value={form.description || ''} onChange={set('description')} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              Are you sure you want to {confirmModal.action} this ledger?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)} disabled={saving}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} loading={saving}>
                Yes, {confirmModal.action}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── COST CENTRE TAB ─────────────────────────
function CostCentresTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listCostCentres();
      let data = Array.isArray(res) ? res : (res.cost_centres || res.data || []);
      // Sort active first, inactive last
      data.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(data);
    }
    catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updateCostCentre(form.id, form);
      else await api.createCostCentre(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteCostCentre(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateCostCentre(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Cost Centre</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: (v, row) => <span style={{ fontWeight: 700, color: !row.is_active ? '#94a3b8' : 'var(--accent)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? "Edit Cost Centre" : "New Cost Centre"} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              {/* Optional and unique. The seeded cost centres carry the client's
                  own codes (ACCT, AD, APP410YN); ones created here can go
                  without one. */}
              <label>Code</label>
              <Input autoFocus value={form.code || ''} onChange={set('code')} placeholder="e.g. ACCT (optional)" />
            </div>
            <div>
              <label>Name</label>
              <Input value={form.name || ''} onChange={set('name')} placeholder="e.g. Account Department" />
            </div>
            <div>
              <label>Description</label>
              <Textarea rows={3} value={form.description || ''} onChange={set('description')} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              Are you sure you want to {confirmModal.action} this cost centre?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)} disabled={saving}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} loading={saving}>
                Yes, {confirmModal.action}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── COST OBJECT TYPES TAB ──────────────────────────────
function CostObjectTypesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listCostObjectTypes();
      let data = Array.isArray(res) ? res : (res.cost_object_types || res.data || []);
      data.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(data);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updateCostObjectType(form.id, form);
      else await api.createCostObjectType(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteCostObjectType(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateCostObjectType(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Cost Object Type</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? 'Edit Cost Object Type' : 'New Cost Object Type'} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Name</label>
              <Input autoFocus value={form.name || ''} onChange={set('name')} placeholder="e.g. Capital Expenditure" />
            </div>
            <div>
              <label>Description</label>
              <Textarea rows={3} value={form.description || ''} onChange={set('description')} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14 }}>
              Are you sure you want to {confirmModal.action} this cost object type?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} disabled={saving}>
                {saving ? 'Processing...' : confirmModal.action === 'deactivate' ? 'Deactivate' : 'Activate'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── CATEGORY ASSET TYPES TAB ───────────────────────────
function CategoryAssetTypesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [costObjectTypes, setCostObjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [catRes, cotRes] = await Promise.all([
        api.listCategoryAssetTypes(),
        api.listCostObjectTypes(true),
      ]);
      let cats = Array.isArray(catRes) ? catRes : (catRes.category_asset_types || []);
      cats.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(cats);
      setCostObjectTypes(Array.isArray(cotRes) ? cotRes : (cotRes.cost_object_types || []));
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        cost_object_type_id: form.cost_object_type_id || null,
      };
      if (form.id) await api.updateCategoryAssetType(form.id, payload);
      else await api.createCategoryAssetType(payload);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteCategoryAssetType(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateCategoryAssetType(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
    setConfirmModal(null);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Category / Asset Type</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</strong> },
          { key: 'cost_object_type_name', label: 'Cost Object Type', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'description', label: 'Description', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? 'Edit Category / Asset Type' : 'New Category / Asset Type'} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Cost Object Type (parent)</label>
              <Select
                value={form.cost_object_type_id || ''}
                onChange={set('cost_object_type_id')}
                options={[
                  { value: '', label: '— None —' },
                  ...costObjectTypes.map(c => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
            <div>
              <label>Name</label>
              <Input autoFocus value={form.name || ''} onChange={set('name')} placeholder="e.g. Heavy Equipment" />
            </div>
            <div>
              <label>Description</label>
              <Textarea rows={3} value={form.description || ''} onChange={set('description')} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14 }}>
              Are you sure you want to {confirmModal.action} this category / asset type?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} disabled={saving}>
                {saving ? 'Processing...' : confirmModal.action === 'deactivate' ? 'Deactivate' : 'Activate'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── COST OBJECTS TAB ───────────────────────────────────
function CostObjectsTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [categoryAssetTypes, setCategoryAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [objRes, catRes] = await Promise.all([
        api.listCostObjects(),
        api.listCategoryAssetTypes(true),
      ]);
      let objs = Array.isArray(objRes) ? objRes : (objRes.cost_objects || []);
      objs.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
      setRows(objs);
      setCategoryAssetTypes(Array.isArray(catRes) ? catRes : (catRes.category_asset_types || []));
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.code || !form.name) { toast.show('Code and Name are required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        name: form.name,
        category_asset_type_id: form.category_asset_type_id || null,
        location: form.location || null,
        state: form.state || null,
        lga: form.lga || null,
        responsible_dept: form.responsible_dept || null,
        responsible_unit: form.responsible_unit || null,
        ownership: form.ownership || null,
        capacity: form.capacity || null,
        is_active: form.is_active !== false,
      };
      if (form.id) await api.updateCostObject(form.id, payload);
      else await api.createCostObject(payload);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const confirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      if (confirmModal.action === 'deactivate') {
        await api.deleteCostObject(confirmModal.id);
        toast.show('Deactivated');
      } else {
        await api.updateCostObject(confirmModal.id, { is_active: true });
        toast.show('Activated');
      }
      load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
    setConfirmModal(null);
  };

  const fieldRow = (label, field, placeholder, half) => (
    <div style={half ? { flex: 1 } : {}}>
      <label>{label}</label>
      <Input value={form[field] || ''} onChange={set(field)} placeholder={placeholder || ''} />
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ is_active: true }); setModal(true); }}>Add Cost Object</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: (v, row) => <strong style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'var(--link-color)' }}>{v}</strong> },
          { key: 'cost_object_type_name', label: 'Cost Object Type', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'inherit', textDecoration: !row.is_active ? 'line-through' : 'none' }}>{v || '—'}</span> },
          { key: 'category_asset_type_name', label: 'Category / Asset Type', render: (v, row) => <span style={{ fontSize: 12, fontWeight: 600, textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v || '—'}</span> },
          { key: 'name', label: 'Name', render: (v, row) => <span style={{ textDecoration: !row.is_active ? 'line-through' : 'none', color: !row.is_active ? '#94a3b8' : 'inherit' }}>{v}</span> },
          { key: 'location', label: 'Location', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'state', label: 'State', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'lga', label: 'LGA', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'responsible_dept', label: 'Responsible Dept.', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'responsible_unit', label: 'Responsible Unit', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'ownership', label: 'Ownership', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'capacity', label: 'Capacity', render: (v, row) => <span style={{ fontSize: 12, color: !row.is_active ? '#94a3b8' : 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" title="Edit" style={{ padding: '6px' }} onClick={() => { setForm(row); setModal(true); }}><EditIcon /></Btn>}
                {row.is_active ? (
                  (access.delete && <Btn variant="danger" title="Deactivate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'deactivate' })}><TrashIcon /></Btn>)
                ) : (
                  (access.edit && <Btn variant="primary" title="Activate" style={{ padding: '6px' }} onClick={() => setConfirmModal({ id, action: 'activate' })}>Activate</Btn>)
                )}
              </div>
            )
          }
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title={form.id ? 'Edit Cost Object' : 'New Cost Object'} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label>Code *</label>
                <Input autoFocus value={form.code || ''} onChange={set('code')} placeholder="e.g. CC-AC-001" />
              </div>
              <div style={{ flex: 2 }}>
                <label>Name *</label>
                <Input value={form.name || ''} onChange={set('name')} placeholder="e.g. HO Reception AC 01" />
              </div>
            </div>
            <div>
              <label>Category / Asset Type</label>
              <Select
                value={form.category_asset_type_id || ''}
                onChange={set('category_asset_type_id')}
                options={[
                  { value: '', label: '— None —' },
                  ...categoryAssetTypes.map(c => ({ value: c.id, label: c.cost_object_type_name ? `${c.cost_object_type_name} › ${c.name}` : c.name })),
                ]}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {fieldRow('Location', 'location', 'e.g. Port Harcourt', true)}
              {fieldRow('State', 'state', 'e.g. Rivers', true)}
              {fieldRow('LGA', 'lga', 'e.g. Port Harcourt', true)}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {fieldRow('Responsible Dept.', 'responsible_dept', 'e.g. Facilities & Accommodation Management', true)}
              {fieldRow('Responsible Unit', 'responsible_unit', 'e.g. Facility Maintenance Unit', true)}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {fieldRow('Ownership', 'ownership', 'e.g. Company Owned', true)}
              {fieldRow('Capacity', 'capacity', 'e.g. 2 HP', true)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <Modal title="Confirm Action" onClose={() => setConfirmModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ margin: 0, fontSize: 14 }}>
              Are you sure you want to {confirmModal.action} this cost object?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn variant={confirmModal.action === 'deactivate' ? 'danger' : 'primary'} onClick={confirmAction} disabled={saving}>
                {saving ? 'Processing...' : confirmModal.action === 'deactivate' ? 'Deactivate' : 'Activate'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── DISCIPLINES TAB MANAGEMENT ─────────────────────────
function DisciplinesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listDisciplines(); setRows(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updateDiscipline(form.id, form);
      else await api.createDiscipline(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deleteDiscipline(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Discipline</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          { key: 'description', label: 'Description', render: v => <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Discipline" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} placeholder="e.g. Structural" />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} placeholder="e.g. STR" />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── COST CATEGORIES TAB ─────────────────────
function CategoriesTab({ toast, access = {} }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listCategories(); setCats(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.cost_category_id) await api.updateCategory(form.cost_category_id, form);
      else await api.createCategory({ ...form, sort_order: parseInt(form.sort_order) || 0 });
      toast.show('Category saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete category?')) return;
    try { await api.deleteCategory(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  const roots = cats.filter(c => !c.parent_id);
  const children = pid => cats.filter(c => c.parent_id === pid);
  const parentOpts = [{ value: '', label: '— Root Category —' }, ...roots.map(c => ({ value: c.cost_category_id, label: `${c.code} – ${c.name}` }))];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ sort_order: 0 }); setModal(true); }}>+ Add Category</Btn>}
      </div>
      <Card style={{ borderRadius: 10 }}>
        {loading ? (
          <RNLLoader title="Loading Categories..." subtitle="Please wait" />
        ) : (
          roots.map(r => (
            <div key={r.cost_category_id}>
              {/* Parent row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', minWidth: 60 }}>{r.code}</span>
                <span style={{ fontWeight: 700, flex: 1 }}>{r.name}</span>
                {access.add && <Btn variant="ghost" size="sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setForm({ parent_id: r.cost_category_id, sort_order: 0 }); setModal(true); }}>+ Child</Btn>}
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setForm({ ...r }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => del(r.cost_category_id)}>Del</Btn>}
              </div>
              {/* Children rows */}
              {children(r.cost_category_id).map(c => (
                <div key={c.cost_category_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px 8px 36px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--border-light)', fontSize: 14 }}>└</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', minWidth: 60 }}>{c.code}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{c.name}</span>
                  {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setForm({ ...c }); setModal(true); }}>Edit</Btn>}
                  {access.delete && <Btn variant="danger" size="sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => del(c.cost_category_id)}>Del</Btn>}
                </div>
              ))}
            </div>
          ))
        )}
        {!loading && cats.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>No categories yet. Create your first cost category.</div>
        )}
      </Card>

      {modal && (
        <Modal title="Cost Category" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} />
              <Select label="Parent Category" value={form.parent_id || ''} onChange={v => setForm(f => ({ ...f, parent_id: v || null }))} options={parentOpts} />
              <Input label="Sort Order" type="number" value={form.sort_order ?? '0'} onChange={set('sort_order')} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── DMR CATEGORIES TAB ─────────────────────
function DMRCategoriesTab({ toast, access = {} }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listDmrCategories(); setCats(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updateDmrCategory(form.id, form);
      else await api.createDmrCategory(form);
      toast.show('Category saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete category?')) return;
    try { await api.deleteDmrCategory(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Category</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          { key: 'description', label: 'Description', render: v => <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={cats} />
      </Card>

      {modal && (
        <Modal title="Category" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Name" required value={form.name || ''} onChange={set('name')} />
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

const GearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

function exportMaterialsExcel(rows) {
  const headers = ['Code', 'Name', 'Category', 'Unit', 'Std. Rate (₦)', 'Lead Days', 'Status', 'Description'];
  const data = rows.map(r => [
    r.code || '',
    r.name || '',
    r.category || '',
    r.unit_of_measure || '',
    r.standard_rate != null ? parseFloat(r.standard_rate) : '',
    r.standard_lead_time_days != null ? r.standard_lead_time_days : '',
    r.is_active ? 'Active' : 'Inactive',
    r.description || '',
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

  // Column widths
  ws['!cols'] = [12, 32, 18, 10, 16, 12, 10, 36].map(w => ({ wch: w }));

  // Style every cell: header = navy bg + white bold text, data = borders
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      const isHeader = R === 0;
      ws[addr].s = {
        font: { bold: isHeader, color: { rgb: isHeader ? 'FFFFFF' : '1E293B' }, name: 'Calibri', sz: isHeader ? 11 : 10 },
        fill: { fgColor: { rgb: isHeader ? '0B318B' : (R % 2 === 0 ? 'FFFFFF' : 'F8FAFC') }, patternType: 'solid' },
        alignment: { vertical: 'center', horizontal: C >= 4 && C <= 5 ? 'right' : 'left', wrapText: false },
        border: {
          top:    { style: 'thin', color: { rgb: 'CBD5E1' } },
          bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
          left:   { style: 'thin', color: { rgb: 'CBD5E1' } },
          right:  { style: 'thin', color: { rgb: 'CBD5E1' } },
        },
      };
    }
  }

  // Format rate column as number
  for (let R = 1; R <= range.e.r; R++) {
    const addr = XLSX.utils.encode_cell({ r: R, c: 4 });
    if (ws[addr] && ws[addr].v !== '') {
      ws[addr].t = 'n';
      ws[addr].z = '#,##0.00';
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Materials');
  XLSX.writeFile(wb, 'materials_export.xlsx');
}

// ─── MATERIALS TAB ───────────────────────────
function MaterialsTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]); // unfiltered, for nextMaterialCode
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [importModal, setImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [searchVersion, setSearchVersion] = useState(0);
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);

  // View detail modal
  const [viewRow, setViewRow] = useState(null);
  const [viewDepts, setViewDepts] = useState([]);
  const [viewDeptsLoading, setViewDeptsLoading] = useState(false);

  const openViewModal = async (row) => {
    setViewRow(row);
    setViewDepts([]);
    setViewDeptsLoading(true);
    try {
      const mapped = await api.getMaterialDepartments(row.material_id);
      setViewDepts(Array.isArray(mapped) ? mapped : []);
    } catch { }
    setViewDeptsLoading(false);
  };

  // Department mapping modal
  const [mappingRow, setMappingRow] = useState(null);   // material being mapped
  const [allDepts, setAllDepts] = useState([]);
  const [mappedDeptIds, setMappedDeptIds] = useState(new Set());
  const [mappingBusy, setMappingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.listMaterials(page.skip, page.limit, appliedSearch);
      setRows(Array.isArray(d) ? d : []);
      setTotalCount(d?.totalCount != null ? d.totalCount : (Array.isArray(d) ? d.length : 0));
    } catch { }
    setLoading(false);
  }, [page, appliedSearch, searchVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate load for all codes (used only by nextMaterialCode; no limit, no search)
  const loadAllCodes = async () => {
    try {
      const d = await api.listMaterials(0, undefined, '');
      setAllRows(Array.isArray(d) ? d : []);
    } catch { }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAllCodes(); }, []); // once on mount

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const nextMaterialCode = () => {
    const nums = allRows
      .map(r => (r.code || '').match(/^MAT-(\d+)$/i))
      .filter(Boolean)
      .map(m => parseInt(m[1], 10));
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    return `MAT-${String(next).padStart(4, '0')}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      if (form.material_id) await api.updateMaterial(form.material_id, form);
      else await api.createMaterial(form);
      toast.show('Material saved');
      setModal(false);
      load();
      loadAllCodes();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const triggerSearch = () => {
    setAppliedSearch(search.trim());
    setPage(p => ({ ...p, skip: 0 }));
    setSearchVersion(v => v + 1);
  };

  const clearSearch = () => {
    setSearch('');
    setAppliedSearch('');
    setPage(p => ({ ...p, skip: 0 }));
    setSearchVersion(v => v + 1);
  };

  const openMappingModal = async (row) => {
    setMappingRow(row);
    setMappingBusy(true);
    try {
      const [depts, mapped] = await Promise.all([
        api.listDepartments(0, 200),
        api.getMaterialDepartments(row.material_id),
      ]);
      const deptList = Array.isArray(depts) ? depts : (depts?.departments || []);
      setAllDepts(deptList);
      setMappedDeptIds(new Set((Array.isArray(mapped) ? mapped : []).map(d => d.id || d.department_id)));
    } catch (e) { toast.show(e.message, 'error'); setMappingRow(null); }
    setMappingBusy(false);
  };

  const toggleDept = (id) => setMappedDeptIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const saveMappings = async () => {
    setMappingBusy(true);
    try {
      await api.setMaterialDepartments(mappingRow.material_id, Array.from(mappedDeptIds));
      toast.show('Department mappings saved');
      setMappingRow(null);
    } catch (e) { toast.show(e.message, 'error'); }
    setMappingBusy(false);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && triggerSearch()}
            placeholder="Search by code, name or category…"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font)', width: 300, outline: 'none' }}
          />
          <button
            onClick={triggerSearch}
            style={{ padding: '7px 18px', background: '#0b318b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}
          >
            Search
          </button>
          {appliedSearch && (
            <button
              onClick={clearSearch}
              style={{ padding: '7px 12px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
            >
              ✕ Clear
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={() => exportMaterialsExcel(allRows)} style={{ background: '#0b318b', borderColor: '#0b318b', color: '#fff' }}>⬇ Export Excel</Btn>
          {access.add && <Btn onClick={downloadTemplate} style={{ background: '#0b318b', borderColor: '#0b318b', color: '#fff' }}>⬇ Download Template</Btn>}
          {access.add && <Btn onClick={() => setImportModal(true)} style={{ background: '#0b318b', borderColor: '#0b318b', color: '#fff' }}>📥 Import from File</Btn>}
          {access.add && <Btn onClick={() => { setForm({ code: nextMaterialCode() }); setModal(true); }}>Add Material</Btn>}
        </div>
      </div>

      {appliedSearch && (
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
          {totalCount} result{totalCount !== 1 ? 's' : ''} for "<strong>{appliedSearch}</strong>"
        </div>
      )}

      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          { key: 'category', label: 'Category' },
          { key: 'unit_of_measure', label: 'Unit' },
          { key: 'standard_rate', label: 'Std. Rate', render: v => <span style={{ fontFamily: 'var(--mono)' }}>₦{parseFloat(v || 0).toLocaleString()}</span> },
          { key: 'standard_lead_time_days', label: 'Lead Days' },
          { key: 'is_active', label: 'Status', render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge> },
          {
            key: 'material_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {row.is_active
                  ? (access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={async () => { try { await api.deleteMaterial(id); toast.show('Deactivated'); load(); loadAllCodes(); } catch (e) { toast.show(e.message, 'error'); } }}>Deactivate</Btn>)
                  : (access.edit && <Btn variant="success" size="sm" style={{ padding: '3px 8px' }} onClick={async () => { try { await api.reactivateMaterial(id); toast.show('Reactivated'); load(); loadAllCodes(); } catch (e) { toast.show(e.message, 'error'); } }}>Reactivate</Btn>)
                }
                <button
                  title="View material details"
                  onClick={() => openViewModal(row)}
                  style={{ padding: '3px 7px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#475569' }}
                >
                  <EyeIcon />
                </button>
                <button
                  title="Map to departments"
                  onClick={() => openMappingModal(row)}
                  style={{ padding: '3px 7px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#475569' }}
                >
                  <GearIcon />
                </button>
              </div>
            )
          },
        ]} rows={rows} />
        <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
      </Card>

      {modal && (
        <Modal title={form.material_id ? 'Edit Material' : 'New Material'} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Code (auto-generated)" required value={form.code || ''} onChange={set('code')}
                readOnly={!form.material_id}
                style={!form.material_id ? { background: '#f1f5f9', color: '#475569', cursor: 'default' } : {}}
              />
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Unit of Measure" required value={form.unit_of_measure || ''} onChange={set('unit_of_measure')} />
              <Input label="Standard Rate" type="number" value={form.standard_rate || ''} onChange={set('standard_rate')} />
              <Input label="Category" value={form.category || ''} onChange={set('category')} />
              <Input label="Lead Time (days)" type="number" value={form.standard_lead_time_days || ''} onChange={set('standard_lead_time_days')} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}

      {viewRow && (
        <Modal title={`Material Details — ${viewRow.code}`} onClose={() => setViewRow(null)} width={800}>
          {viewDeptsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
              Loading details…
            </div>
          ) : (
            <>
              {/* Two-column field grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', borderTop: '1px solid #f1f5f9' }}>
                {[
                  { label: 'Code',            value: viewRow.code },
                  { label: 'Name',            value: viewRow.name },
                  { label: 'Category',        value: viewRow.category || '—' },
                  { label: 'Unit of Measure', value: viewRow.unit_of_measure },
                  { label: 'Standard Rate',   value: viewRow.standard_rate != null ? `₦${parseFloat(viewRow.standard_rate).toLocaleString('en-NG', { minimumFractionDigits: 2 })}` : '—' },
                  { label: 'Lead Time',       value: viewRow.standard_lead_time_days != null ? `${viewRow.standard_lead_time_days} day(s)` : '—' },
                  { label: 'Status',          value: viewRow.is_active ? 'Active' : 'Inactive', accent: viewRow.is_active },
                  { label: 'Description',     value: viewRow.description || '—' },
                ].map(({ label, value, accent }) => (
                  <div key={label} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: 13.5, color: accent === true ? '#16a34a' : accent === false ? '#dc2626' : '#1e293b', fontWeight: accent != null ? 700 : 400 }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Department Mappings */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '2px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Department Mappings ({viewDepts.length})
                  </span>
                  <button
                    onClick={() => { setViewRow(null); openMappingModal(viewRow); }}
                    style={{ fontSize: 12, color: '#0b318b', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}
                  >
                    + Manage
                  </button>
                </div>
                {viewDepts.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#94a3b8', background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px dashed #cbd5e1' }}>
                    No departments mapped yet — click <strong>+ Manage</strong> to assign.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {viewDepts.map(d => (
                      <span key={d.id} style={{ padding: '4px 12px', background: '#e0e7ff', color: '#3730a3', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1px solid #c7d2fe' }}>
                        {d.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                {access.edit && <Btn variant="ghost" onClick={() => { setViewRow(null); setForm({ ...viewRow }); setModal(true); }}>Edit</Btn>}
                <Btn onClick={() => setViewRow(null)}>Close</Btn>
              </div>
            </>
          )}
        </Modal>
      )}

      {mappingRow && (
        <Modal
          title={`Department Mapping — ${mappingRow.code}`}
          onClose={() => !mappingBusy && setMappingRow(null)}
          width={500}
        >
          <p style={{ margin: '0 0 4px', fontSize: 13, color: '#475569' }}>
            <strong>{mappingRow.name}</strong>
          </p>
          <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#64748b' }}>
            Select the departments that will use this material. A material can be shared by multiple departments.
          </p>

          {mappingBusy && allDepts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#64748b', fontSize: 13 }}>Loading departments…</div>
          ) : (
            <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 0' }}>
              {allDepts.length === 0 && (
                <div style={{ padding: '16px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>No departments found.</div>
              )}
              {allDepts.map(dept => {
                const checked = mappedDeptIds.has(dept.id);
                return (
                  <label key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: checked ? '#f0f4ff' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleDept(dept.id)}
                      style={{ width: 16, height: 16, accentColor: '#0b318b', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13.5, fontWeight: checked ? 600 : 400, color: '#1e293b' }}>{dept.name}</span>
                  </label>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
              {mappedDeptIds.size} department{mappedDeptIds.size !== 1 ? 's' : ''} selected
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#0b318b', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={allDepts.length > 0 && mappedDeptIds.size === allDepts.length}
                ref={el => { if (el) el.indeterminate = mappedDeptIds.size > 0 && mappedDeptIds.size < allDepts.length; }}
                onChange={() => {
                  if (mappedDeptIds.size === allDepts.length) {
                    setMappedDeptIds(new Set());
                  } else {
                    setMappedDeptIds(new Set(allDepts.map(d => d.id)));
                  }
                }}
                style={{ width: 15, height: 15, accentColor: '#0b318b', cursor: 'pointer' }}
              />
              {mappedDeptIds.size === allDepts.length && allDepts.length > 0 ? 'Uncheck All' : 'Check All'}
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setMappingRow(null)} disabled={mappingBusy}>Cancel</Btn>
            <Btn onClick={saveMappings} loading={mappingBusy}
              style={{ background: '#0b318b', borderColor: '#0b318b', color: '#fff' }}
            >
              Save Mappings
            </Btn>
          </div>
        </Modal>
      )}

      {importModal && (
        <MaterialImportModal
          toast={toast}
          onClose={() => setImportModal(false)}
          onImported={() => { load(); loadAllCodes(); }}
        />
      )}
    </>
  );
}

// ─── ACCOUNT LEDGERS TAB ─────────────────────────
function AccountLedgersTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listAccountLedgers(); setRows(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.ledger_id) await api.updateAccountLedger(form.ledger_id, form);
      else await api.createAccountLedger(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deleteAccountLedger(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Ledger</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          { key: 'description', label: 'Description', render: v => <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
          {
            key: 'ledger_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Account Ledger" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── BUDGET CONTROL LEVELS TAB ─────────────────────────
function BudgetControlLevelsTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listBudgetControlLevels(); setRows(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.budget_control_level_id) await api.updateBudgetControlLevel(form.budget_control_level_id, form);
      else await api.createBudgetControlLevel(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deleteBudgetControlLevel(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Level</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          {
            key: 'budget_control_level_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Budget Control Level" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Name" required value={form.name || ''} onChange={set('name')} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── COST GROUPS TAB ─────────────────────────
function CostGroupsTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([api.listCostGroups(), api.listBudgetControlLevels()]);
      setRows(Array.isArray(d) ? d : []);
      setLevels(Array.isArray(l) ? l : []);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.cost_group_id) await api.updateCostGroup(form.cost_group_id, form);
      else await api.createCostGroup(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deleteCostGroup(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  const levelOpts = levels.map(l => ({ value: l.budget_control_level_id, label: l.name }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Cost Group</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          {
            key: 'budget_control_level_id', label: 'Budget Level', render: v => {
              const lvl = levels.find(l => l.budget_control_level_id === v);
              return lvl ? lvl.name : '—';
            }
          },
          {
            key: 'cost_group_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Cost Group" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} />
            </FormGrid>
            <Select label="Budget Control Level" required value={form.budget_control_level_id || ''} onChange={v => set('budget_control_level_id')(v || null)} options={[{ value: '', label: 'Select level...' }, ...levelOpts]} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── EXPENSE CATEGORIES TAB ─────────────────────────
// Sub headings beneath an expense category. Deliberately its own card rather
// than nested inside the Expense Categories screen: they are maintained
// independently, and a flat list with the parent shown per row is easier to
// scan than an expandable tree.
function ExpenseSubCategoriesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [viewDesc, setViewDesc] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subs, cats] = await Promise.all([
        api.listExpenseSubCategories(),
        api.listExpenseCategories(),
      ]);
      setRows(Array.isArray(subs) ? subs : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.expense_category_id) { toast.show('Choose the parent expense category.', 'error'); return; }
    setSaving(true);
    try {
      if (form.expense_sub_category_id) await api.updateExpenseSubCategory(form.expense_sub_category_id, form);
      else await api.createExpenseSubCategory(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(errorText(e), 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    // Requisition lines that used it keep their history — the server sets the
    // link to NULL rather than removing the line.
    if (!window.confirm('Delete this sub category? Requisitions that used it keep their lines.')) return;
    try { await api.deleteExpenseSubCategory(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(errorText(e), 'error'); }
  };

  const catOpts = categories.map(c => ({ value: c.expense_category_id, label: `${c.code} · ${c.name}` }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Expense Sub Category</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          {
            key: 'expense_category_id', label: 'Expense Category', render: (v, row) => {
              if (row.expense_category_name) return row.expense_category_name;
              const c = categories.find(x => x.expense_category_id === v);
              return c ? c.name : '—';
            }
          },
          { key: 'description', label: 'Description', render: v => <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</div> },
          {
            key: 'expense_sub_category_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => setViewDesc(row)} title="View Description">👁</Btn>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} emptyMsg="No sub categories yet. An expense category without any simply has no sub category picker on a requisition." />
      </Card>

      {modal && (
        <Modal title="Expense Sub Category" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Select label="Expense Category" required value={form.expense_category_id || ''} onChange={v => set('expense_category_id')(v || null)} options={[{ value: '', label: 'Select category...' }, ...catOpts]} />
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              The code must be unique across the system. The name only has to be unique within its parent category.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}

      {viewDesc && (
        <Modal title="Expense Sub Category Details" onClose={() => setViewDesc(null)} width={550}>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 8, fontSize: 14 }}>
            <strong>Code</strong><span>{viewDesc.code || '—'}</span>
            <strong>Name</strong><span>{viewDesc.name || '—'}</span>
            <strong>Expense Category</strong><span>{viewDesc.expense_category_name || '—'}</span>
            <strong>Description</strong><span>{viewDesc.description || '—'}</span>
          </div>
        </Modal>
      )}
    </>
  );
}

function ExpenseCategoriesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [viewDesc, setViewDesc] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, g, disc] = await Promise.all([
        api.listExpenseCategories(),
        api.listCostGroups(),
        api.listDisciplines()
      ]);
      setRows(Array.isArray(d) ? d : []);
      setGroups(Array.isArray(g) ? g : []);
      setDisciplines(Array.isArray(disc) ? disc : []);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.expense_category_id) await api.updateExpenseCategory(form.expense_category_id, form);
      else await api.createExpenseCategory(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deleteExpenseCategory(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  const groupOpts = groups.map(g => ({ value: g.cost_group_id, label: g.name }));
  const discOpts = disciplines.map(d => ({ value: d.id, label: d.name }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Expense Category</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          {
            key: 'cost_group_id', label: 'Cost Group', render: v => {
              const g = groups.find(x => x.cost_group_id === v);
              return g ? g.name : '—';
            }
          },
          {
            key: 'discipline_id', label: 'Discipline', render: v => {
              const d = disciplines.find(x => x.id === v);
              return d ? d.name : '—';
            }
          },
          { key: 'description', label: 'Description', render: v => <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</div> },
          {
            key: 'expense_category_id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => setViewDesc(row)} title="View Description">👁</Btn>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Expense Category" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} />
              <Select label="Cost Group" value={form.cost_group_id || ''} onChange={v => set('cost_group_id')(v || null)} options={[{ value: '', label: 'Select group...' }, ...groupOpts]} />
              <Select label="Discipline" value={form.discipline_id || ''} onChange={v => set('discipline_id')(v || null)} options={[{ value: '', label: 'Select discipline...' }, ...discOpts]} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}

      {viewDesc && (
        <Modal title="Expense Category Details" onClose={() => setViewDesc(null)} width={550}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'var(--text-primary)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Code:</strong>
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{viewDesc.code}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Name:</strong>
              <span>{viewDesc.name}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Cost Group:</strong>
              <span>{(() => {
                const g = groups.find(x => x.cost_group_id === viewDesc.cost_group_id);
                return g ? g.name : '—';
              })()}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Discipline:</strong>
              <span>{(() => {
                const d = disciplines.find(x => x.id === viewDesc.discipline_id);
                return d ? d.name : '—';
              })()}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Description:</strong>
              <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{viewDesc.description || 'No description provided.'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <Btn onClick={() => setViewDesc(null)}>Close</Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── SUPPLIERS TAB ─────────────────────────
function SuppliersTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewDesc, setViewDesc] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listSuppliers(); setRows(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.supplier_id) await api.updateSupplier(form.supplier_id, form);
      else await api.createSupplier(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deleteSupplier(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({ category: 'vendor' }); setModal(true); }}>Add Supplier</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          { key: 'category', label: 'Category', render: v => <span style={{ textTransform: 'capitalize' }}>{v}</span> },
          { key: 'contact_person', label: 'Contact Person' },
          { key: 'phone', label: 'Phone' },
          { key: 'tax_id', label: 'Tax ID', render: v => <span style={{ fontFamily: 'var(--mono)' }}>{v || '—'}</span> },
          { key: 'bank_name', label: 'Bank Name', render: v => v || '—' },
          { key: 'bank_account', label: 'Account No', render: v => <span style={{ fontFamily: 'var(--mono)' }}>{v || '—'}</span> },
          {
            key: 'status', label: 'Status', render: v => (
              <span style={{
                padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                background: v === 'active' ? '#e6f4ea' : '#fce8e6',
                color: v === 'active' ? '#137333' : '#c5221f'
              }}>
                {v}
              </span>
            )
          },
          {
            key: 'supplier_id', label: 'Actions', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => setViewDesc(row)}>👁 View</Btn>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Supplier" onClose={() => setModal(false)} width={600}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Name" required value={form.name || ''} onChange={set('name')} />
              <Input label="Code" required value={form.code || ''} onChange={set('code')} />
              <Select label="Category" required value={form.category || 'vendor'} onChange={set('category')} options={[
                { value: 'vendor', label: 'Vendor' }, { value: 'subcontractor', label: 'Subcontractor' }, { value: 'logistics', label: 'Logistics' }, { value: 'labour', label: 'Labour' }
              ]} />
              <Input label="Contact Person" value={form.contact_person || ''} onChange={set('contact_person')} />
              <Input label="Email" value={form.email || ''} onChange={set('email')} />
              <Input label="Phone" value={form.phone || ''} onChange={set('phone')} />
              <Input label="City" value={form.city || ''} onChange={set('city')} />
              <Input label="State" value={form.state || ''} onChange={set('state')} />
              <Input label="Tax ID" value={form.tax_id || ''} onChange={set('tax_id')} />
              <Input label="Bank Name" value={form.bank_name || ''} onChange={set('bank_name')} />
              <Input label="Bank Account" value={form.bank_account || ''} onChange={set('bank_account')} />
              <Input label="Payment Terms" value={form.payment_terms || ''} onChange={set('payment_terms')} />
            </FormGrid>
            <Input label="Address" value={form.address || ''} onChange={set('address')} />
            <Textarea label="Notes" value={form.notes || ''} onChange={set('notes')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}

      {viewDesc && (
        <Modal title="Supplier Details" onClose={() => setViewDesc(null)} width={550}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'var(--text-primary)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Code:</strong>
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{viewDesc.code}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Name:</strong>
              <span>{viewDesc.name}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Category:</strong>
              <span style={{ textTransform: 'capitalize' }}>{viewDesc.category}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Status:</strong>
              <span style={{ textTransform: 'capitalize' }}>{viewDesc.status}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Contact:</strong>
              <span>{viewDesc.contact_person || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Email:</strong>
              <span>{viewDesc.email || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Phone:</strong>
              <span>{viewDesc.phone || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Address:</strong>
              <span>{[viewDesc.address, viewDesc.city, viewDesc.state].filter(Boolean).join(', ') || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Tax ID:</strong>
              <span style={{ fontFamily: 'var(--mono)' }}>{viewDesc.tax_id || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Bank:</strong>
              <span>{viewDesc.bank_name || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Account No:</strong>
              <span style={{ fontFamily: 'var(--mono)' }}>{viewDesc.bank_account || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Payment Terms:</strong>
              <span>{viewDesc.payment_terms || '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Lead Time:</strong>
              <span>{viewDesc.lead_time_days ? `${viewDesc.lead_time_days} days` : '—'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Notes:</strong>
              <span style={{ whiteSpace: 'pre-wrap' }}>{viewDesc.notes || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Btn onClick={() => setViewDesc(null)}>Close</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}


// ─── PACKAGE TYPES TAB ───────────────────────
function PackageTypesTab({ toast, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.getPackageTypes(); setRows(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.updatePackageType(form.id, form);
      else await api.createPackageType(form);
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.deletePackageType(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {access.add && <Btn onClick={() => { setForm({}); setModal(true); }}>Add Package Type</Btn>}
      </div>
      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
          { key: 'description', label: 'Description', render: v => <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            )
          },
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Package Type" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Name" required value={form.name || ''} onChange={set('name')} />
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── UNIT OF MEASURE TAB ─────────────────────
const CATEGORY_OPTIONS = [
  { value: 'count',     label: 'Count' },
  { value: 'length',    label: 'Length' },
  { value: 'area',      label: 'Area' },
  { value: 'volume',    label: 'Volume' },
  { value: 'weight',    label: 'Weight' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'time',      label: 'Time' },
  { value: 'other',     label: 'Other' },
];

function UOMTab({ toast, access = {} }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');

  const load = async () => {
    setLoading(true);
    try { const d = await api.getUOMOptions(false); setRows(Array.isArray(d) ? d : []); }
    catch { } setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.symbol?.trim() || !form.name?.trim()) {
      toast.show('Symbol and Name are required', 'error'); return;
    }
    setSaving(true);
    try {
      if (form.id) await api.updateUOM(form.id, { symbol: form.symbol, name: form.name, category: form.category, is_active: form.is_active });
      else         await api.createUOM({ symbol: form.symbol, name: form.name, category: form.category || 'other', is_active: true });
      toast.show('Saved'); setModal(false); load();
    } catch (e) { toast.show(errorText(e), 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this UOM entry? This cannot be undone.')) return;
    try { await api.deleteUOM(id); toast.show('Deleted'); load(); }
    catch (e) { toast.show(errorText(e), 'error'); }
  };

  const toggle = async (row) => {
    try {
      await api.updateUOM(row.id, { is_active: !row.is_active });
      toast.show(row.is_active ? 'Deactivated' : 'Activated'); load();
    } catch (e) { toast.show(errorText(e), 'error'); }
  };

  const visible = rows.filter(r =>
    !search.trim() ||
    r.symbol.toLowerCase().includes(search.toLowerCase()) ||
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <ListToolbar value={search} onChange={setSearch} placeholder="Search symbol, name or category…" shown={visible.length} total={rows.length}>
        {access.add && <Btn onClick={() => { setForm({ category: 'other', is_active: true }); setModal(true); }}>+ Add UOM</Btn>}
      </ListToolbar>

      <Card style={{ borderRadius: 0, overflow: 'hidden', padding: 0, border: '1px solid black', boxShadow: 'none' }}>
        <Table loading={loading} cols={[
          {
            key: 'symbol', label: 'Symbol',
            render: v => <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)', fontSize: 13 }}>{v}</span>,
          },
          { key: 'name',     label: 'Name',     render: v => <strong>{v}</strong> },
          {
            key: 'category', label: 'Category',
            render: v => {
              const colors = { count: 'blue', length: 'purple', area: 'indigo', volume: 'cyan', weight: 'orange', packaging: 'yellow', time: 'green', other: 'gray' };
              return <Badge color={colors[v] || 'gray'} style={{ textTransform: 'capitalize' }}>{v}</Badge>;
            },
          },
          {
            key: 'is_active', label: 'Status',
            render: v => <Badge color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Badge>,
          },
          {
            key: 'id', label: '', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>}
                {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '3px 8px' }} onClick={() => toggle(row)}>
                  {row.is_active ? 'Deactivate' : 'Activate'}
                </Btn>}
                {access.delete && <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => del(id)}>Del</Btn>}
              </div>
            ),
          },
        ]} rows={visible} />
      </Card>

      {modal && (
        <Modal title={form.id ? 'Edit Unit of Measure' : 'Add Unit of Measure'} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input
                label="Symbol" required
                value={form.symbol || ''}
                onChange={set('symbol')}
                placeholder="e.g. kgs"
              />
              <Input
                label="Full Name" required
                value={form.name || ''}
                onChange={set('name')}
                placeholder="e.g. Kilograms"
              />
            </FormGrid>
            <Select
              label="Category"
              value={form.category || 'other'}
              onChange={set('category')}
              options={CATEGORY_OPTIONS}
            />
            {form.id && (
              <Select
                label="Status"
                value={form.is_active ? 'true' : 'false'}
                onChange={v => setForm(f => ({ ...f, is_active: v === 'true' }))}
                options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── MAIN ─────────────────────────────────────
export default function AdminPage({ toast, user }) {
  const [activeTab, setActiveTab] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isUACRoute = location.pathname.startsWith('/admin/user-access');

  // Sync tab from URL if present (useful for direct links / back navigation)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && !activeTab) setActiveTab(tab);
    if (isUACRoute && activeTab !== 'user_access_control') setActiveTab('user_access_control');
  }, [searchParams, activeTab, isUACRoute]);

  const [workflowSelectedDef, setWorkflowSelectedDef] = useState(null);
  // Which card is open inside Request Management. Held here rather than in
  // the tab because the back bar above it has to know whether Back means
  // "leave the section" or "leave Admin".
  const [requestSection, setRequestSection] = useState(null);
  const activeProjectId = searchParams.get('id');
  const activeCategory = searchParams.get('category');

  const adminCards = [
    { id: 'projects', label: 'Project Setup', icon: '🏗', desc: 'Manage projects, budgets & BOQ', roles: ['super_admin'] },
    { id: 'user_access_control', label: 'User Access Control', icon: '🔐', desc: 'Enterprise-grade access management — roles, permissions, sites, audit & more', roles: ['super_admin'] },
    { id: 'users', label: 'Users Management', icon: '👥', desc: 'Manage user accounts', roles: ['super_admin'] },
    { id: 'roles', label: 'Roles Control', icon: '🛡', desc: 'System roles & permissions', roles: ['super_admin'] },
    { id: 'departments', label: 'Departments', icon: '🏢', desc: 'Company departments', roles: ['super_admin'] },
    { id: 'pr_types', label: 'Purchase Request Types', icon: '📋', desc: 'Purchase request categories', roles: ['super_admin'] },
    { id: 'consideration_statuses', label: 'Consideration Statuses', icon: '⏳', desc: 'Why a requisition is parked in the Cost Control queue', roles: ['super_admin'] },
    { id: 'request_management', label: 'Request Management', icon: '🗂️', desc: 'Manage requisitions and payment vouchers, including their approval records', roles: ['super_admin'] },
    { id: 'pr_ledgers', label: 'Purchase Request Ledger', icon: '📔', desc: 'HR requisition payment categories', roles: ['super_admin'] },
    { id: 'payment_methods', label: 'Voucher Creation Methods', icon: '💳', desc: 'Types of payment voucher that can be raised', roles: ['super_admin'] },
    { id: 'cost_centres', label: 'Cost Centre', icon: '🏷', desc: 'Administration requisition cost centres', roles: ['super_admin'] },
    { id: 'cost_object_types', label: 'Cost Object Types', icon: '🗃', desc: 'Logistics requisition cost object type classifications', roles: ['super_admin'] },
    { id: 'category_asset_types', label: 'Category / Asset Types', icon: '📂', desc: 'Sub-categories under each cost object type for logistics', roles: ['super_admin'] },
    { id: 'cost_objects', label: 'Cost Objects', icon: '🏷', desc: 'Physical/logical asset records with code, location and ownership details', roles: ['super_admin'] },
    { id: 'disciplines', label: 'Disciplines', icon: '📐', desc: 'Engineering disciplines', roles: ['super_admin'] },
    { id: 'categories', label: 'Cost Categories', icon: '🗂', desc: 'Cost tracking categories', roles: ['super_admin'] },
    { id: 'uom', label: 'Units of Measure', icon: '📏', desc: 'Manage measurement units used across all forms', roles: ['super_admin'] },
    { id: 'materials', label: 'Materials Master', icon: '🧱', desc: 'Standard materials database', roles: ['super_admin'] },
    { id: 'workflows', label: 'Workflow Configs', icon: '⚙️', desc: 'Approval workflows & steps', roles: ['super_admin'] },
    { id: 'account_ledgers', label: 'Account Ledgers', icon: '📓', desc: 'General ledger accounts', roles: ['super_admin'] },
    { id: 'package_types', label: 'BOQ Package Types', icon: '📦', desc: 'Pre-seeded package groupings', roles: ['super_admin'] },
    { id: 'budget_control_levels', label: 'Budget Control Levels', icon: '🎚', desc: 'Budget hierarchy levels', roles: ['super_admin'] },
    { id: 'cost_groups', label: 'Cost Groups', icon: '📑', desc: 'Groupings of cost codes', roles: ['super_admin'] },
    { id: 'expense_categories', label: 'Expense Categories', icon: '🧾', desc: 'Categorize expenses', roles: ['super_admin'] },
    { id: 'expense_sub_categories', label: 'Expense Sub Categories', icon: '🗂️', desc: 'Sub headings under a category', roles: ['super_admin'] },
    { id: 'dmr_categories', label: 'DMR Categories', icon: '🛒', desc: 'Direct Material Register Categories', roles: ['super_admin'] },
    { id: 'suppliers', label: 'Suppliers', icon: '🚚', desc: 'Manage vendors and suppliers', roles: ['super_admin'] },
    // moved here from the dashboard and sidebar; opens inside System Administration like the other cards
    { id: 'settings', label: 'Settings', icon: '⚙', desc: 'Payroll parameters, OT rates, PAYE table and payers.', roles: ['super_admin'] },
    // Cards in ADMIN_CARDS (and Request Management) open to anyone ticked View for them in the Permission
    // Matrix (Users too: admin_users:view); the rest — User Access Control, Roles, Workflows,
    // Settings — stay super-admin only.
  ].filter(c => hasRole(user, c.roles) || ((ADMIN_CARDS.includes(c.id) || c.id === 'request_management' || c.id === 'users') && can(user, `admin_${c.id}:view`)));

  // A tab reached by address (?tab=…) still has to be one of this user's cards.
  if (activeTab && user && !adminCards.some(c => c.id === activeTab)) {
    return (
      <div className="fade-in" style={{ padding: 40, textAlign: 'center' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>No access to this section</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, margin: '0 0 18px' }}>
          Ask an administrator to grant it in User Access Control → Permission Matrix.
        </p>
        <Btn variant="secondary" onClick={() => { setActiveTab(null); setSearchParams({}); }}>Back to System Administration</Btn>
      </div>
    );
  }

  if (activeTab === 'user_access_control') {
    return (
      <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <HeaderBack onClick={() => { setActiveTab(null); setSearchParams({}); navigate('/admin'); }} label="Back to Admin" />
        <div style={{ padding: '14px 26px 0', flexShrink: 0 }}>
          <div className="flex-mobile-col" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#e4ebefdf', border: '1px solid black', borderRadius: 14 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Currently managing: <strong style={{ color: 'var(--text-primary)' }}>🔐 User Access Control</strong>
            </span>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, marginTop: 12 }}>
          <UserAccessControl toast={toast} user={user} />
        </div>
      </div>
    );
  }

  if (activeTab) {
    const card = adminCards.find(c => c.id === activeTab);
    const backLabel = activeTab === 'projects' && activeProjectId && activeCategory
      ? 'Back to Project Setup'
      : activeTab === 'projects' && activeProjectId
        ? 'Back to project list'
        : activeTab === 'workflows' && workflowSelectedDef
          ? 'Back to List'
          : activeTab === 'request_management' && requestSection
            ? 'Back to Request Management'
            : 'Back to Admin';
    const handleBack = () => {
      if (activeTab === 'projects' && activeProjectId && activeCategory) {
        setSearchParams({ tab: 'projects', id: activeProjectId });
      } else if (activeTab === 'projects' && activeProjectId) {
        setSearchParams({ tab: 'projects' });
      } else if (activeTab === 'workflows' && workflowSelectedDef) {
        setWorkflowSelectedDef(null);
      } else if (activeTab === 'request_management' && requestSection) {
        setRequestSection(null);
      } else {
        setRequestSection(null);
        setActiveTab(null);
        setSearchParams({});
      }
    };
    return (
      <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <HeaderBack onClick={handleBack} label={backLabel} />
        <div style={{ padding: '24px 26px 0' }}>
          <div className="flex-mobile-col" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#e4ebefdf', border: '1px solid black', borderRadius: 14 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Currently managing: <strong style={{ color: 'var(--text-primary)' }}>
                {card?.icon} {activeCategory === 'DMR' ? 'DMR Management' : activeCategory === 'LABOUR' ? 'Labour Management' : card?.label}
              </strong>
            </span>
          </div>
        </div>
        <div style={{ flex: 1, padding: '20px 26px' }}>
          {activeTab === 'projects' && <AdminProjectsTab toast={toast} user={user} access={adminAccess(user, 'projects')} />}
          {activeTab === 'settings' && <ComingSoonPage moduleName="Settings" />}
          {activeTab === 'users' && <UsersTab toast={toast} me={user} />}
          {activeTab === 'roles' && <RolesTab toast={toast} />}
          {activeTab === 'departments' && <DepartmentsTab toast={toast} access={adminAccess(user, 'departments')} />}
          {activeTab === 'pr_types' && <PRTypesTab toast={toast} access={adminAccess(user, 'pr_types')} />}
          {activeTab === 'consideration_statuses' && <ConsiderationStatusesTab toast={toast} access={adminAccess(user, 'consideration_statuses')} />}
          {activeTab === 'request_management' && (
            <RequestManagementTab toast={toast} section={requestSection} setSection={setRequestSection} access={adminAccess(user, 'request_management')} />
          )}
          {activeTab === 'pr_ledgers' && <LedgersTab toast={toast} access={adminAccess(user, 'pr_ledgers')} />}
          {activeTab === 'payment_methods' && <PaymentMethodsTab toast={toast} access={adminAccess(user, 'payment_methods')} />}
          {activeTab === 'cost_centres' && <CostCentresTab toast={toast} access={adminAccess(user, 'cost_centres')} />}
          {activeTab === 'cost_object_types' && <CostObjectTypesTab toast={toast} access={adminAccess(user, 'cost_object_types')} />}
          {activeTab === 'category_asset_types' && <CategoryAssetTypesTab toast={toast} access={adminAccess(user, 'category_asset_types')} />}
          {activeTab === 'cost_objects' && <CostObjectsTab toast={toast} access={adminAccess(user, 'cost_objects')} />}
          {activeTab === 'disciplines' && <DisciplinesTab toast={toast} access={adminAccess(user, 'disciplines')} />}
          {activeTab === 'categories' && <CategoriesTab toast={toast} access={adminAccess(user, 'categories')} />}
          {activeTab === 'uom' && <UOMTab toast={toast} access={adminAccess(user, 'uom')} />}
          {activeTab === 'materials' && <MaterialsTab toast={toast} access={adminAccess(user, 'materials')} />}
          {activeTab === 'workflows' && <AdminWorkflowsTab toast={toast} selectedDef={workflowSelectedDef} setSelectedDef={setWorkflowSelectedDef} />}
          {activeTab === 'account_ledgers' && <AccountLedgersTab toast={toast} access={adminAccess(user, 'account_ledgers')} />}
          {activeTab === 'package_types' && <PackageTypesTab toast={toast} access={adminAccess(user, 'package_types')} />}
          {activeTab === 'budget_control_levels' && <BudgetControlLevelsTab toast={toast} access={adminAccess(user, 'budget_control_levels')} />}
          {activeTab === 'cost_groups' && <CostGroupsTab toast={toast} access={adminAccess(user, 'cost_groups')} />}
          {activeTab === 'expense_categories' && <ExpenseCategoriesTab toast={toast} access={adminAccess(user, 'expense_categories')} />}
          {activeTab === 'expense_sub_categories' && <ExpenseSubCategoriesTab toast={toast} access={adminAccess(user, 'expense_sub_categories')} />}
          {activeTab === 'dmr_categories' && <DMRCategoriesTab toast={toast} access={adminAccess(user, 'dmr_categories')} />}
          {activeTab === 'suppliers' && <SuppliersTab toast={toast} access={adminAccess(user, 'suppliers')} />}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '24px 26px' }}>
      <SectionHeader title="Administration" subtitle="System Configuration & Management" />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16, marginTop: 24 }}>
        {adminCards.map(card => {
          const isUAC = card.id === 'user_access_control';
          return (
            <div key={card.id} style={{
              background: isUAC ? 'linear-gradient(135deg, #1e3a5f 0%, #0f2240 100%)' : '#e2e8f0',
              border: isUAC ? '2px solid #2563eb' : '2px solid #000',
              borderRadius: '12px',
              padding: '24px',
              cursor: 'pointer',
              boxShadow: isUAC ? '0 20px 40px rgba(37, 99, 235, 0.35)' : '0 15px 30px rgba(0, 0, 0, 0.55)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex', flexDirection: 'column', gap: 12,
              gridColumn: isUAC ? 'span 2' : undefined,
            }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = isUAC ? '0 30px 60px rgba(37, 99, 235, 0.5)' : '0 36px 64px rgba(0, 0, 0, 0.65)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                if (!isUAC) { e.currentTarget.style.background = '#cbd5e1'; e.currentTarget.style.borderColor = 'var(--accent)'; }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = isUAC ? '0 20px 40px rgba(37, 99, 235, 0.35)' : '0 15px 30px rgba(0, 0, 0, 0.55)';
                e.currentTarget.style.transform = 'none';
                if (!isUAC) { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.borderColor = '#000'; }
              }}
              onClick={() => {
                if (card.id === 'user_access_control') {
                  navigate('/admin/user-access/dashboard');
                } else {
                  setActiveTab(card.id);
                  setSearchParams({ tab: card.id });
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ fontSize: isUAC ? 36 : 32 }}>{card.icon}</div>
                {isUAC && (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 5 }}>{card.label}</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{card.desc}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                      {['User Master', 'Role Master', 'Permission Matrix', 'Audit Trail', 'Access Requests'].map(tag => (
                        <span key={tag} style={{ fontSize: 10.5, background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', borderRadius: 999, padding: '3px 9px', fontWeight: 600 }}>{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {!isUAC && (
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{card.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{card.desc}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
